import { usePageState } from "@/navigation/NavigationProvider";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import BoardWorkspace from "@/components/BoardWorkspace";
import { TypedDateInput } from "@/components/TypedDateInput";
import { getProjectModuleAvailability } from "@/utils/projectFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const weekLabel = (iso) => {
  const [year, month, day] = iso.split("-");
  return `${month}/${day}`;
};

export default function AnalyticsPage() {
  const { canAccessAnalytics, user } = useAuth();
  const modules = getProjectModuleAvailability(user?.features);
  const [range, setRange] = usePageState("range", "month");
  const [startDate, setStartDate] = usePageState("startDate", "");
  const [endDate, setEndDate] = usePageState("endDate", "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (range === "custom" && (!startDate || !endDate)) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics`, {
        params: { range, start_date: startDate, end_date: endDate },
      });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analytics could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [range, startDate, endDate]);

  useEffect(() => {
    if (canAccessAnalytics) load();
  }, [canAccessAnalytics, load, modules.permits, modules.locates]);

  if (!canAccessAnalytics) return <Navigate replace to="/master-project-board" />;

  const performance = data?.project_performance;
  const problems = data?.operational_problems;
  const workload = data?.workload;
  const weeks = performance?.completed_by_week || [];
  const maxWeek = Math.max(1, ...weeks.map((week) => week.count));

  return (
    <BoardWorkspace
      eyebrow="Restricted company insight"
      headingTestId="analytics-heading"
      subtitle="Operational health for Company Owner/Admin and Office/Admin."
      title="Analytics"
    >
      <section className="project-notes-panel" data-testid="analytics-workspace">
        <div className="analytics-legend" data-testid="analytics-legend">
          <span className="analytics-scope-tag is-today">As of today</span>
          <span className="analytics-scope-tag is-period">Selected period</span>
          <p>
            Snapshot metrics reflect current state and ignore the range. Period metrics respect the
            selected dates.
          </p>
        </div>
        <div className="communication-filters">
          <select
            data-testid="analytics-range-select"
            onChange={(event) => setRange(event.target.value)}
            value={range}
          >
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="quarter">This quarter</option>
            <option value="year">This year</option>
            <option value="custom">Custom</option>
          </select>
          {range === "custom" && (
            <>
              <TypedDateInput data-testid="analytics-start-date" onChange={setStartDate} value={startDate} />
              <TypedDateInput data-testid="analytics-end-date" onChange={setEndDate} value={endDate} />
            </>
          )}
          <button data-testid="analytics-apply-button" onClick={load} type="button">
            Apply
          </button>
        </div>

        {loading && !data && (
          <p data-testid="analytics-loading">Loading analytics…</p>
        )}

        {data && (
          <>
            <section className="analytics-section" data-testid="analytics-project-performance">
              <h2>Project Performance</h2>
              <div className="analytics-metrics">
                <Metric label="Completed projects" scope="period" value={performance.completed} testId="analytics-completed" />
                <Metric
                  label="Avg. completion time"
                  scope="period"
                  value={performance.average_completion_days == null ? "—" : `${performance.average_completion_days} days`}
                  testId="analytics-avg-completion"
                />
                <Metric label="Past due projects" scope="today" value={performance.past_due} testId="analytics-past-due" />
                <Metric label="Roadblocked projects" scope="today" value={performance.roadblocked} testId="analytics-perf-roadblocked" />
              </div>
              <div className="analytics-week-chart" data-testid="analytics-completed-by-week">
                <h3>Projects completed by week</h3>
                {weeks.some((week) => week.count > 0) ? (
                  <div className="analytics-week-bars">
                    {weeks.map((week) => (
                      <div className="analytics-week-bar" key={week.week_start} title={`${week.count} completed`}>
                        <span className="analytics-week-count">{week.count}</span>
                        <i style={{ height: `${(week.count / maxWeek) * 100}%` }} />
                        <span className="analytics-week-label">{weekLabel(week.week_start)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="analytics-empty">No projects completed in this period.</p>
                )}
              </div>
            </section>

            <section className="analytics-section" data-testid="analytics-operational-problems">
              <h2>Operational Problems</h2>
              <div className="analytics-metrics">
                {modules.permits && <Metric label="Permit problems" scope="today" value={problems.permit_problems} testId="analytics-permit-problems" link="/permits-locates" />}
                {modules.locates && <Metric label="811 Locate problems" scope="today" value={problems.locate_problems} testId="analytics-locate-problems" link="/permits-locates" />}
                {modules.locates && <Metric label="Water Locate problems" scope="today" value={problems.water_locate_problems} testId="analytics-water-problems" link="/permits-locates" />}
                <Metric label="Inactive projects" scope="today" value={problems.inactive_projects} testId="analytics-inactive" link="/master-project-board" />
                <Metric label="New roadblocks" scope="period" value={problems.new_roadblocks} testId="analytics-new-roadblocks" link="/action-queue" />
              </div>
            </section>

            <section className="analytics-section" data-testid="analytics-workload">
              <h2>Workload</h2>
              <div className="analytics-metrics">
                <Metric label="In progress" scope="today" value={workload.in_progress} testId="analytics-in-progress" />
                <Metric label="Scheduled" scope="today" value={workload.scheduled} testId="analytics-scheduled" />
                <Metric label="Roadblocked" scope="today" value={workload.roadblocked} testId="analytics-workload-roadblocked" />
                <Metric label="Field complete" scope="today" value={workload.field_complete} testId="analytics-field-complete" />
                <Metric label="Completed this period" scope="period" value={workload.completed_in_period} testId="analytics-completed-period" />
              </div>
              <div className="analytics-pm-table" data-testid="analytics-pm-workload">
                <h3>PM workload (as of today)</h3>
                {workload.pm_workload.length ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Project Manager</th>
                        <th>Active</th>
                        <th>Past due</th>
                        <th>Roadblocked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workload.pm_workload.map((row) => (
                        <tr key={row.user_id} data-testid={`analytics-pm-row-${row.user_id}`}>
                          <td>{row.name}</td>
                          <td>{row.active}</td>
                          <td>{row.past_due}</td>
                          <td>{row.roadblocked}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="analytics-empty">No projects are assigned to a project manager yet.</p>
                )}
              </div>
              <Link to="/master-project-board">Open Master Board</Link>
            </section>
          </>
        )}
      </section>

    </BoardWorkspace>
  );
}

function Metric({ label, value, scope, testId, link }) {
  const body = (
    <>
      <span className="analytics-metric-label">{label}</span>
      <strong>{value}</strong>
      <span className={`analytics-scope-tag is-${scope === "period" ? "period" : "today"}`}>
        {scope === "period" ? "Selected period" : "As of today"}
      </span>
    </>
  );
  if (link) {
    return (
      <Link className="analytics-metric" data-testid={testId} to={link}>
        {body}
      </Link>
    );
  }
  return (
    <article className="analytics-metric" data-testid={testId}>
      {body}
    </article>
  );
}


